import React from 'react';
import { Helmet } from 'react-helmet-async';
import { Smartphone, Clock, Video, Mail, Calendar, Info } from 'lucide-react';

export default function Konsultationer() {
  return (
    <main className="pt-32 pb-24">
      <Helmet>
        <title>Konsultationer & Videokonsultation | Hellerup Lægehus</title>
        <meta name="description" content="Se vores konsultationstyper: Tid-Samme-Dag (TSD), videokonsultation og e-konsultation. Bestil tid via MinLæge appen eller online her." />
        <meta property="og:title" content="Konsultationer & Videokonsultation | Hellerup Lægehus" />
        <meta property="og:description" content="Se vores konsultationstyper: Tid-Samme-Dag (TSD), videokonsultation og e-konsultation. Bestil tid via MinLæge appen eller online her." />
        <html lang="da-DK" />
      </Helmet>
      <div className="max-w-7xl mx-auto px-6">
        <div className="mb-12">
          <div className="flex items-center gap-4 mb-6">
            <div className="h-[1px] w-12 bg-[#4b717a]" />
            <span className="text-[#4b717a] text-xs tracking-[0.4em] uppercase font-bold">Information</span>
          </div>
          <h1 className="text-5xl md:text-6xl font-sans font-medium text-black mb-8 leading-tight tracking-tight">
            Konsultationer
          </h1>
        </div>

        <div className="max-w-3xl prose prose-slate prose-lg
            prose-headings:font-sans prose-headings:font-medium prose-headings:text-black prose-headings:mt-12 prose-headings:mb-6 prose-headings:leading-tight prose-headings:tracking-tight
            prose-p:text-slate-600 prose-p:leading-relaxed
            prose-a:text-[#4b717a] prose-a:no-underline hover:prose-a:underline
            prose-strong:text-slate-900
            prose-ul:list-disc prose-ol:list-decimal">
          
          <div className="bg-slate-50 p-8 border-l-4 border-[#4b717a] mb-12 not-prose">
            <div className="flex items-start gap-4">
              <Smartphone className="text-[#4b717a] shrink-0 mt-1" size={24} />
              <div>
                <h3 className="text-xl font-sans font-medium text-black mb-2">MinLæge app</h3>
                <p className="text-slate-600 leading-relaxed">
                  Vi anbefaler at downloade app’en <strong>MinLæge</strong>. Denne app giver adgang på både online tidsbestilling, videokonsultation, medicinbestilling og meget mere.
                </p>
              </div>
            </div>
          </div>

          <h2 className="text-3xl font-sans font-medium text-black">Selvbetjening</h2>
          <p>
            Vi opfordrer til at benytte selvbetjening via hjemmesiden eller app’en MinLæge. Selvbetjening anbefales for at mindske presset på telefonerne. Herved kan patienter uden adgang til elektronisk selvbetjening eller patienter med brug for akut hjælp lettest får kontakt til os.
          </p>
          <p>
            Vi ønsker at holde vores tider, så vi ikke lader jer patienter sidde og vente, hvorfor vi kun anbefaler <strong>1-2 problemstillinger per konsultation</strong>. Herved får vi som læger bedre arbejdsvilkår og kan koncentrere os om, at yde en nærværende behandling af højeste kvalitet.
          </p>

          <div className="my-16 border-y border-slate-100 py-12">
            <h2 className="text-3xl font-sans font-medium text-black flex items-center gap-3">
              <Clock className="text-[#4b717a]" size={32} />
              Tid-Samme-Dag (TSD)
            </h2>
            <p className="mt-6">
              Vi bruger bookingsystemet TSD hos lægerne, både til fremmøde konsultationer samt videokonsultationer, primært med henblik på, at afskaffe ventetiden. Systemet bygger på princippet om, at du som patient er garanteret en tid i lægehuset samme dag du henvender dig hvis dette sker <strong>inden kl 10</strong>.
            </p>
            <p>
              Online tiderne hos lægerne bliver tilgængelige dagen før fra kl. 16:00. Du kan selvfølgelig også booke på selve dagen, eller ringe til os mellem kl. 08:00 og 12:00 for at aftale en tid.
            </p>
            <p>
              Vi har rigtig god erfaring med TSD som giver højere service, tilfredshed og fleksibilitet hos patienterne. Vi kan nemmere finde tider til akutte patienter og det giver mindre stressende arbejdsmiljø hos personalet. Vi får færre udeblivelser og minimerer kontakterne til lægevagten.
            </p>
          </div>

          <h3 className="text-2xl font-sans font-medium text-black">Forudbestilte tider ved lægerne bruges kun til følgende:</h3>
          <ul>
            <li>Attester & lægeerklæringer</li>
            <li>Årskontroller af kroniske sygdomme (der må også gerne benyttes Tid-Samme-Dag)</li>
            <li>Samtaler</li>
            <li>Kirurgiske indgreb</li>
            <li>Konsultationer med tolk</li>
          </ul>

          <div className="bg-[#4b717a]/5 p-8 my-12 not-prose">
            <h3 className="text-xl font-sans font-medium text-black mb-4 flex items-center gap-2">
              <Calendar className="text-[#4b717a]" size={20} />
              Konsultationer ved sygeplejerske, jordemoder eller medicinstuderende
            </h3>
            <p className="text-slate-600">
              Kræver ikke en TSD-bookning. Disse tider kan derfor bookes frem i tiden via selvbetjening eller ved at ringe til vores sekretærer.
            </p>
          </div>

          <h2 className="text-3xl font-sans font-medium text-black flex items-center gap-3">
            <Video className="text-[#4b717a]" size={32} />
            Video-Konsultation
          </h2>
          <p>
            Du kan selv booke en tid til videokonsultation via Selvbetjening eller ringe til sekretærerne som vil booke tiden for dig. Der gælder helt de samme regler som ved TSD. Se mere om Video-Konsultation <a href="/video-konsultation">her</a>.
          </p>

          <h2 className="text-3xl font-sans font-medium text-black flex items-center gap-3">
            <Mail className="text-[#4b717a]" size={32} />
            E-mails
          </h2>
          <p>
            Bedst til meget korte spørgsmål og simple problemstillinger. Vi svarer inden for 5 arbejdsdage.
          </p>
        </div>
      </div>
    </main>
  );
}
